import React, { PureComponent } from 'react';
import CompletedRenewalsPortalPage from '../../../presenters/renewals/CompletedRenewalsPortalPage/CompletedRenewalsPortalPage';
import * as api from '../../../../services/api';
import LoadingWidget from '../../../widgets/LoadingWidget/LoadingWidget';
import {
  getQueryStringAsObject,
  serializeAsQueryString,
  getQueryStringParam
} from '../../../../utils/utils';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import filterBoxes from './renewals-archive-filter-boxes';
import { COMPLETED_RENEWALS_PORTAL_URL } from '../../../../route-names';
import {
  DEFAULT_DIRECTION,
  DIRECTION,
  DEFAULT_SELECTED_BRANCH_ROUTE_NUMBER,
  DEFAULT_SELECTED_DISTRICT_NUMBER,
  DEFAULT_ACTIVE_BRANCH_ID
} from '../../../../constants/constants';
import { getBranchIds } from '../../../../utils/api-util';
import { DEFAULT_PAGEABLE } from '../../../../services/_api/shared';
import moment from 'moment/moment';
import { injectIntl } from 'react-intl';
import * as ArchiveConstants from './renewals-archive-constants';

const zipArchivedRenewals = (archivedRenewals) =>
  archivedRenewals.map((archivedRenewal) => ({
    ...archivedRenewal,
    ignoreOnClick: true
  }));

const today = () => moment().startOf('d');

const daysSince = (startDate) => today().diff(startDate, 'days');

const getCustomerApproveDateAfter = () => {
  const dateString = getQueryStringAsObject().customerApproveDateAfter;
  return !!dateString ? dateString : ArchiveConstants.ALL_TIME_DATE_STRING;
};

const getBranchRouteNumberParam = () =>
  getQueryStringParam('routeNumbers') || null;

const getDistrictIdsParam = () => getQueryStringParam('districtIds') || null;

class CompletedRenewalsPortal extends PureComponent {
  constructor(props) {
    super(props);

    const customerApproveDateAfter = getCustomerApproveDateAfter();
    const { activeBranchId, branches, routes, districts } = this.props.appStore;
    const branchIds = getBranchIds(activeBranchId, branches);

    this.state = {
      loading: true,
      archivedRenewals: [],
      filterCounts: filterBoxes(this.props).map(() => 0),
      page: DEFAULT_PAGEABLE.page,
      sortBy: { name: 'customerApproveDate', direction: 'desc' },
      customerApproveDateAfter,
      message: null,
      routes: routes
        .filter((route) => branchIds.includes(route.branchId))
        .map((route) => ({
          ...route,
          branchDesc: branches.find(
            (branch) => branch.branchId === route.branchId
          ).branchDesc
        })),
      allDistricts: districts,
      districts: {}
    };

    this.updateState = this.updateState.bind(this);
    this.onFilterBoxClick = this.onFilterBoxClick.bind(this);
    this.getMappedFilterBoxes = this.getMappedFilterBoxes.bind(this);
    this.getRoutes = this.getRoutes.bind(this);
    this.onCustomerSearch = this.onCustomerSearch.bind(this);
    this.onChangePage = this.onChangePage.bind(this);
    this.onChangeRowsPerPage = this.onChangeRowsPerPage.bind(this);
    this.onRequestSort = this.onRequestSort.bind(this);
    this.getSearchOptions = this.getSearchOptions.bind(this);
    this.fetchCounts = this.fetchCounts.bind(this);
    this.getDistricts = this.getDistricts.bind(this);
    this.updateQueryState = this.updateQueryState.bind(this);
    this.onChangeRoute = this.onChangeRoute.bind(this);
    this.onChangeDistrict = this.onChangeDistrict.bind(this);
    this.onClearFilters = this.onClearFilters.bind(this);
    this.hideDropdown = this.hideDropdown.bind(this);
  }

  componentDidMount() {
    this.updateState();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.location.search !== prevProps.location.search ||
      this.props.appStore.activeBranchId !== prevProps.appStore.activeBranchId
    ) {
      this.updateState(true);
    }
  }

  updateState(doResetPage = false) {
    this.setState(
      {
        loading: true,
        page: doResetPage ? DEFAULT_PAGEABLE.page : this.state.page,
        customerApproveDateAfter: getCustomerApproveDateAfter()
      },
      () => {
        const searchOptions = this.getSearchOptions();

        const archivePromise = api.archivedRenewals.search(
          {
            page: this.state.page,
            sortBy: this.state.sortBy
          },
          searchOptions
        );

        Promise.all([archivePromise, this.fetchCounts()])
          .then(([archiveResponse, countsResponses]) =>
            this.setState({
              archivedRenewals: zipArchivedRenewals(
                archiveResponse.data.content
              ),
              page: {
                size: archiveResponse.data.size,
                totalElements: archiveResponse.data.totalElements,
                number: archiveResponse.data.number,
                numberOfElements: archiveResponse.data.numberOfElements
              },
              filterCounts: countsResponses.map((responses) => responses[0].data),
              pastDueCounts: countsResponses.map((responses) => responses[1].data)
            })
          )
          .catch((err) => {
            console.error('error fetching archived renewals', err);
          })
          .then(() => {
            let districtDict = {};
            if (this.state.routes && this.state.allDistricts) {
              this.state.routes.forEach((route) => {
                let district = this.state.allDistricts.find(
                  (district) => district.districtId === route.districtId
                );
                if (district) {
                  let districtCode = district.code;
                  let districtId = district.districtId;
                  districtDict[districtId] = districtCode;
                }
              });
            }
            this.setState({
              loading: false,
              districts: districtDict
            });
          });
      }
    );
  }

  getSearchOptions() {
    const { activeBranchId, branches } = this.props.appStore;
    return {
      ...getQueryStringAsObject(),
      branchIds: getBranchIds(activeBranchId, branches)
    };
  }

  fetchCounts() {
    const { activeBranchId, branches } = this.props.appStore;
    return Promise.all(
      filterBoxes(this.props).map((box) => {
        const classifications =
          !!box.classifications && box.classifications.length > 0
            ? box.classifications.map((classification) => classification.name)
            : undefined;

        const customerApproveDateAfter = !!box.customerApproveDateAfter
          ? box.customerApproveDateAfter
          : this.state.customerApproveDateAfter;

        const countParams = {
          classifications,
          branchIds: getBranchIds(activeBranchId, branches),
          customerApproveDateAfter,
          districtIds: getDistrictIdsParam(),
          routeNumbers: getBranchRouteNumberParam()
        };

        const counts = [api.archivedRenewals.count(countParams)];
        counts.push(classifications === undefined ? api.archivedRenewals.count({ ...countParams, pastDue: true }) : '');

        return Promise.all(counts);
      })
    );
  }

  getMappedFilterBoxes() {
    return filterBoxes(this.props).map((box, index) => {
      const boxAlreadyFiltersTime = !!box.customerApproveDateAfter;

      const pastDueCount = this.state.pastDueCounts && this.state.pastDueCounts[index];

      const newBox = {
        ...box,
        count: this.state.filterCounts[index],
        customerApproveDateAfter: boxAlreadyFiltersTime
          ? box.customerApproveDateAfter
          : this.state.customerApproveDateAfter,
        subtitle: boxAlreadyFiltersTime && this.props.intl.formatMessage({ id: 'Past Due' }),
        subvalue: !boxAlreadyFiltersTime ? this.props.intl.formatMessage({ id: 'all_time' }) : pastDueCount
      };

      return {
        ...newBox,
        onClick: () => this.onFilterBoxClick(newBox)
      };
    });
  }

  onFilterBoxClick(box) {
    const classifications =
      !!box.classifications && box.classifications.length > 0
        ? box.classifications.map((classification) => classification.name)
        : undefined;

    const customerApproveDateAfter =
      box.customerApproveDateAfter === ArchiveConstants.ALL_TIME_DATE_STRING
        ? undefined
        : box.customerApproveDateAfter;

    const queryString = serializeAsQueryString(
      {
        customerApproveDateAfter,
        classifications
      },
      true
    );
    this.updateQueryState(queryString);
  }

  getRoutes() {
    return [
      {
        routeNumber: DEFAULT_SELECTED_BRANCH_ROUTE_NUMBER,
        display: this.props.intl.formatMessage({ id: 'all_routes' })
      },
      ...this.state.routes
        .sort((a, b) => a.branchId - b.branchId)
        .sort((a, b) => a.routeNumber - b.routeNumber)
        .map((route) => ({
          routeNumber: `${route.routeNumber}`,
          display:
            this.props.appStore.activeBranchId === DEFAULT_ACTIVE_BRANCH_ID
              ? `${route.branchDesc} ${this.props.intl.formatMessage({
                  id: 'route'
                })}# ${route.routeNumber}`
              : `${route.routeNumber}`
        }))
    ];
  }

  onChangeRoute(selectedRouteNumber) {
    const routeNumbers =
      selectedRouteNumber !== DEFAULT_SELECTED_BRANCH_ROUTE_NUMBER
        ? selectedRouteNumber
        : null;
    const queryString = serializeAsQueryString(
      {
        districtIds: getDistrictIdsParam(),
        routeNumbers
      },
      true
    );
    this.updateQueryState(queryString);
  }

  getDistricts() {
    return [
      {
        districtId: DEFAULT_SELECTED_DISTRICT_NUMBER,
        display: this.props.intl.formatMessage({ id: 'all_districts' })
      },
      ...Object.keys(this.state.districts)
        .sort((a, b) => this.state.districts[a] - this.state.districts[b])
        .map((districtId) => ({
          districtId: districtId,
          display: this.state.districts[districtId]
        }))
    ];
  }

  onChangeDistrict(selectedDistrictNumber) {
    const districtIds =
      selectedDistrictNumber !== DEFAULT_SELECTED_DISTRICT_NUMBER
        ? selectedDistrictNumber
        : null;
    const queryString = serializeAsQueryString(
      {
        routeNumbers: getBranchRouteNumberParam(),
        districtIds
      },
      true
    );
    this.updateQueryState(queryString);
  }

  onClearFilters() {
    this.updateQueryState('');
  }

  hideDropdown() {
    return this.props.appStore.activeBranchId === DEFAULT_ACTIVE_BRANCH_ID;
  }

  updateQueryState(queryString) {
    this.props.history.push(`${COMPLETED_RENEWALS_PORTAL_URL}?${queryString}`);
  }

  onCustomerSearch(searchString) {
    this.updateQueryState(`search=${searchString}`);
  }

  onChangePage(number) {
    this.setState(
      {
        page: {
          ...this.state.page,
          number
        }
      },
      this.updateState
    );
  }

  onChangeRowsPerPage(rows) {
    this.setState(
      {
        page: {
          ...this.state.page,
          size: rows,
          number: 0
        }
      },
      this.updateState
    );
  }

  onRequestSort(name) {
    const direction =
      this.state.sortBy.name === name
        ? DIRECTION[this.state.sortBy.direction].opposite
        : DEFAULT_DIRECTION;

    this.setState(
      {
        sortBy: {
          name,
          direction
        }
      },
      this.updateState
    );
  }

  render() {
    return this.state.loading ? (
      <LoadingWidget />
    ) : (
      <div>
        <CompletedRenewalsPortalPage
          onCustomerSearch={this.onCustomerSearch}
          archivedRenewals={this.state.archivedRenewals}
          filterBoxes={this.getMappedFilterBoxes()}
          routes={this.getRoutes()}
          districts={this.getDistricts()}
          onChangeRoute={this.onChangeRoute}
          onChangeDistrict={this.onChangeDistrict}
          page={this.state.page}
          onChangePage={this.onChangePage}
          onChangeRowsPerPage={this.onChangeRowsPerPage}
          sortBy={this.state.sortBy}
          onRequestSort={this.onRequestSort}
          selectedRouteNumber={
            getBranchRouteNumberParam() || DEFAULT_SELECTED_BRANCH_ROUTE_NUMBER
          }
          selectedDistrictNumber={
            getDistrictIdsParam() || DEFAULT_SELECTED_DISTRICT_NUMBER
          }
          onClearFilters={this.onClearFilters}
          hideDropdown={this.hideDropdown()}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  appStore: state.get('app').toJS()
});

export default withRouter(
  connect(mapStateToProps)(injectIntl(CompletedRenewalsPortal))
);
